import type { WizardFarm } from '../../utils/farmWizard'
import { listFarmProcessingSites, type FarmProcessingSiteOption } from '../../utils/farmProcessingSites'

export { listFarmProcessingSites, type FarmProcessingSiteOption } from '../../utils/farmProcessingSites'

export type LotDestinationOption = {
  farmId: string
  farmLabel: string
  productionUnitId: string
  productionUnitLabel: string
  processingSiteId: string
  processingSiteLabel: string
  targetLocationType: 'production-unit' | 'farm' | 'processing-site'
  label: string
}

type BuildLotDestinationOptionsParams = {
  preferredFarmId?: string
  preferredProductionUnitId?: string
  preferredProcessingSiteId?: string
  targetLocationType?: 'production-unit' | 'farm' | 'processing-site'
}

const compareText = (left: string, right: string): number => (
  left.localeCompare(right, undefined, { sensitivity: 'base' })
)

const normalizeSearchValue = (value: string): string => value.trim().toLowerCase()

const getSortedProductionUnits = (farm: WizardFarm) => (
  (farm.productionUnits ?? [])
    .filter((unit) => Boolean(unit.id))
    .slice()
    .sort((left, right) => compareText(left.name ?? left.id ?? '', right.name ?? right.id ?? ''))
)

const buildSearchText = (option: LotDestinationOption): string => (
  normalizeSearchValue([
    option.farmLabel,
    option.productionUnitLabel,
    option.processingSiteLabel,
    option.label,
    option.farmId,
    option.productionUnitId,
    option.processingSiteId
  ].join(' '))
)

const getOptionTargetLabel = (option: LotDestinationOption): string => (
  option.targetLocationType === 'processing-site'
    ? option.processingSiteLabel
    : option.productionUnitLabel
)

const getDestinationRank = (
  option: LotDestinationOption,
  params: BuildLotDestinationOptionsParams
): number => {
  if (params.targetLocationType === 'processing-site') {
    if (
      params.preferredFarmId &&
      params.preferredProcessingSiteId &&
      option.farmId === params.preferredFarmId &&
      option.processingSiteId === params.preferredProcessingSiteId
    ) {
      return 0
    }

    if (params.preferredFarmId && option.farmId === params.preferredFarmId) {
      return 1
    }

    return 2
  }

  if (params.targetLocationType === 'farm') {
    return params.preferredFarmId && option.farmId === params.preferredFarmId ? 0 : 1
  }

  if (
    params.preferredFarmId &&
    params.preferredProductionUnitId &&
    option.farmId === params.preferredFarmId &&
    option.productionUnitId === params.preferredProductionUnitId
  ) {
    return 0
  }

  if (params.preferredFarmId && option.farmId === params.preferredFarmId) {
    return 1
  }

  return 2
}

export const buildLotDestinationOptions = (
  farms: WizardFarm[],
  params: BuildLotDestinationOptionsParams = {}
): LotDestinationOption[] => {
  const options = farms.reduce<LotDestinationOption[]>((allOptions, farm) => {
    if (!farm.id) return allOptions

    const farmLabel = farm.name ?? farm.id

    if (params.targetLocationType === 'farm') {
      allOptions.push({
        farmId: farm.id,
        farmLabel,
        productionUnitId: '',
        productionUnitLabel: '',
        processingSiteId: '',
        processingSiteLabel: '',
        targetLocationType: 'farm',
        label: farmLabel
      })
      return allOptions
    }

    if (params.targetLocationType === 'processing-site') {
      listFarmProcessingSites(farm).forEach((site) => {
        allOptions.push({
          farmId: farm.id,
          farmLabel,
          productionUnitId: '',
          productionUnitLabel: '',
          processingSiteId: site.id,
          processingSiteLabel: site.label,
          targetLocationType: 'processing-site',
          label: `${farmLabel} / ${site.label}`
        })
      })
      return allOptions
    }

    getSortedProductionUnits(farm).forEach((unit) => {
      if (!unit.id) return

      const productionUnitLabel = unit.name ?? unit.id
      allOptions.push({
        farmId: farm.id,
        farmLabel,
        productionUnitId: unit.id,
        productionUnitLabel,
        processingSiteId: '',
        processingSiteLabel: '',
        targetLocationType: 'production-unit',
        label: `${farmLabel} / ${productionUnitLabel}`
      })
    })

    return allOptions
  }, [])

  return options.sort((left, right) => {
    const rankDiff = getDestinationRank(left, params) - getDestinationRank(right, params)
    if (rankDiff !== 0) return rankDiff

    const farmDiff = compareText(left.farmLabel, right.farmLabel)
    if (farmDiff !== 0) return farmDiff

    return compareText(getOptionTargetLabel(left), getOptionTargetLabel(right))
  })
}

export const filterLotDestinationOptions = (
  options: LotDestinationOption[],
  query: string,
  limit = options.length
): LotDestinationOption[] => {
  const normalizedQuery = normalizeSearchValue(query)
  if (!normalizedQuery) {
    return options.slice(0, limit)
  }

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean)

  return options
    .map((option) => {
      const searchText = buildSearchText(option)
      if (!queryTokens.every((token) => searchText.includes(token))) {
        return null
      }

      const label = normalizeSearchValue(option.label)
      const farmLabel = normalizeSearchValue(option.farmLabel)
      const targetLabel = normalizeSearchValue(getOptionTargetLabel(option))
      const score = (
        label.startsWith(normalizedQuery) ||
        farmLabel.startsWith(normalizedQuery) ||
        targetLabel.startsWith(normalizedQuery)
      )
        ? 0
        : 1

      return { option, score }
    })
    .filter((entry): entry is { option: LotDestinationOption; score: number } => Boolean(entry))
    .sort((left, right) => {
      const scoreDiff = left.score - right.score
      if (scoreDiff !== 0) return scoreDiff

      const farmDiff = compareText(left.option.farmLabel, right.option.farmLabel)
      if (farmDiff !== 0) return farmDiff

      return compareText(getOptionTargetLabel(left.option), getOptionTargetLabel(right.option))
    })
    .slice(0, limit)
    .map((entry) => entry.option)
}